import { DateTime } from "luxon";
import {
  _throw,
  IllegalArgumentError,
  IllegalStateError,
} from "shared-ts-utils-core";
import {
  DateObjectUnits,
  DateTimeJSOptions,
  DateTimeOptions,
  DateTimeUnit,
  ToISOTimeOptions,
} from "luxon";
import { DateTimeISO } from "shared-ts-types";

export class DateTimeUtils {
  static toTruncatedIso(time: DateTime, unit: DateTimeUnit): string {
    switch (unit.toLowerCase()) {
      case "year":
        return time.year.toString();
      case "quarter":
        return time.toFormat("yyyy-qq");
      case "month":
        return time.toFormat("yyyy-LL");
      case "week":
        return time.toFormat("yyyy-WW");
      case "day":
        return time.toFormat("yyyy-LL-dd");
      case "hour":
        return time.toFormat("yyyy-LL-ddTHH");
      case "minute":
        return time.toFormat("yyyy-LL-ddTHH:mm");
      case "second":
        return time.toFormat("yyyy-LL-ddTHH:mm:ss");
      case "millisecond":
        return time.toFormat("yyyy-LL-ddTHH:mm:ss:SSS");
    }
    throw new IllegalArgumentError(`Unsupported unit: ${unit}`);
  }
  static toIso(obj: DateTime, opts?: ToISOTimeOptions): DateTimeISO {
    return (
      obj.toISO() ?? _throw(new IllegalStateError(`Invalid DateTime: ${obj}`))
    );
  }

  static toUtcIso(obj: DateTime, opts?: ToISOTimeOptions): DateTimeISO {
    return (
      obj.toUTC().toISO() ??
      _throw(new IllegalStateError(`Invalid DateTime: ${obj}`))
    );
  }

  static fromISO(text: string, opts?: DateTimeOptions): DateTime<true>;
  static fromISO(text: undefined, opts?: DateTimeOptions): undefined;
  static fromISO(text: null, opts?: DateTimeOptions): null;
  static fromISO(
    text: string | null | undefined,
    opts?: DateTimeOptions
  ): DateTime<true> | null | undefined;

  static fromISO(
    text: string | null | undefined,
    opts?: DateTimeOptions
  ): DateTime<true> | null | undefined {
    if (text === undefined) {
      return undefined;
    }
    if (text === null) {
      return null;
    }
    const dt = DateTime.fromISO(text, opts);
    if (!dt.isValid) {
      throw new IllegalStateError(`Invalid DateTime: ${text}`);
    }
    return dt as DateTime<true>;
  }

  static fromNullableISO(text?: string | null, opts?: DateTimeOptions): DateTime<true> | null {
    return text ? (DateTime.fromISO(text, opts) as DateTime<true>) : null;
  }

  static fromMillis(millis: number, opts?: DateTimeOptions): DateTime<true> {
    return (DateTime.fromMillis(millis, opts) ??
      _throw(
        new IllegalStateError(`Invalid DateTime: ${millis}`)
      )) as DateTime<true>;
  }

  static fromObject(
    obj: DateObjectUnits,
    opts?: DateTimeJSOptions
  ): DateTime<true> {
    return (DateTime.fromObject(obj, opts) ??
      _throw(
        new IllegalStateError(`Invalid DateTime: ${obj}`)
      )) as DateTime<true>;
  }

  static formatDateString(
    date: string,
    layout: "normal" | "short" = "normal"
  ): string {
    const dateTime = this.fromISO(date);
    return this.dateAsString(dateTime, layout);
  }

  static dateAsString(
    date: DateTime | Date,
    layout: "normal" | "short" = "normal"
  ): string {
    let dateTime = date as DateTime;
    if (date instanceof Date) {
      dateTime = DateTime.fromJSDate(date);
    }

    return dateTime.year < 10000
      ? dateTime
        .toFormat("dd" + (layout === "short" ? ".MM." : " MMM ") + " yyyy")
        .capitalizeFirstLetter()
      : "∞";
  }

  static formatDateTimeString(
    date: string,
    layout: "normal" | "short" = "normal"
  ): string {
    const dateTime = this.fromISO(date);
    return this.dateTimeAsString(dateTime, layout);
  }

  static dateTimeAsString(
    date: DateTime | Date,
    layout: "normal" | "short" = "normal"
  ): string {
    let dateTime = date as DateTime;
    if (date instanceof Date) {
      dateTime = DateTime.fromJSDate(date);
    }

    return dateTime.year < 10000
      ? dateTime
          .toFormat(layout === "short"
            ? "dd.MM. yyyy HH:mm"
            : "dd MMM yyyy HH:mm:ss"
          )
          .capitalizeFirstLetter()
      : "∞";
  }
}
